import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import QRCode from 'react-qr-code';
// import { Mail, Phone, CalendarRange, MapPin } from 'lucide-react';
import { NumberFormatter } from '@mantine/core';

import { ETable } from 'libs/ui';
import { numberToWords, humanizeText } from 'utils/utility';
import { useUsersData } from 'libs/data-access/src/api/usersData/useUsersData';
import { IHotelOnlineBookingList, IUserDetailsForInvoice } from 'libs/data-access/types';

import HotelRoomDetailsSimple from './HotelRoomDetailsSimple';


type IProps = {
    ticketDetails: IHotelOnlineBookingList;
};

export default function HotelTicketInvoice({ ticketDetails }: IProps) {

    const { getUsersData } = useUsersData();

    // const invoiceRef: any = useRef(null);
    const [userDetails, setUserDetails] = useState<IUserDetailsForInvoice>({
        id: 0,
        name: "",
        emailId: "",
        phoneNumber: "",
        affiliateDetailForInvoice: {
            country: null,
            city: null,
            state: null,
            pinCode: null,
            firstAddress: null,
            secondAddress: null,
            companyName: null,
        }
    });

    const [fareDetails, setFareDetails] = useState({
        hotelBaseFare: 0,
        hotelTax: 0,
        serviceCharges: 0,
        otherCharges: 0,
        totalGst: 0,
        totalFare: 0,
    });

    useEffect(() => {
        if (ticketDetails?.id) {
            getUserDetails();
        };
        manageFareBreakup();
    }, [ticketDetails]);


    // HANDLER FUNCTIONS
    // Calculate fare breakdown similar to flight component
    const manageFareBreakup = () => {
        if (!ticketDetails?.paymentOrderResponse) return;

        const payment = ticketDetails.paymentOrderResponse;
        // console.log("Payment details: ", payment);

        let fareBreakup = {
            hotelBaseFare: 0,
            hotelTax: 0,
            serviceCharges: 0,
            otherCharges: 0,
            totalGst: 0,
            totalFare: 0,
        };

        // Hotel base fare
        fareBreakup.hotelBaseFare =
            (payment.hotelTotalAmount || 0)
            +
            (payment.adminBaseCharge || 0)
            +
            (payment.affiliateBaseCharge || 0);

        // Hotel tax
        fareBreakup.hotelTax = 0;

        // Service charges
        fareBreakup.serviceCharges =
            // (payment.adminBaseCharge || 0)
            // +
            // (payment.adminServiceCharge || 0)
            +
            (payment.adminBookingCharge || 0)
            +
            (payment.affiliateBaseCharge || 0)
            +
            (payment.affiliateServiceCharge || 0)
            +
            (payment.affiliateBookingCharge || 0);

        // Other charges
        fareBreakup.otherCharges = payment.adminOtherCharge || 0;

        // Total GST
        fareBreakup.totalGst = payment.adminTotalGst || 0;

        // Total fare
        fareBreakup.totalFare = (payment?.hotelTotalAmount || 0)
            +
            // (payment?.taxt || 0)                        
            // +
            (payment?.adminBaseCharge || 0)
            +
            (payment?.adminServiceCharge || 0)
            +
            (payment?.adminBookingCharge || 0)
            +
            (payment?.adminOtherCharge || 0)
            +
            (payment?.adminTotalGst || 0)
            +
            (payment?.affiliateBaseCharge || 0)
            +
            (payment?.affiliateServiceCharge || 0)
            +
            (payment?.affiliateBookingCharge || 0);

        setFareDetails({
            ...fareBreakup
        });

        // console.log("fareBreakup: ", fareBreakup);
    };

    // Safe access to nested properties
    const getHotelName = () => {
        return ticketDetails?.paymentOrderResponse?.hotel?.name || "N/A";
    };

    const getHotelAddress = () => {
        return ticketDetails?.paymentOrderResponse?.hotel?.address || "N/A";
    };

    const getHotelPhones = () => {
        const phones = ticketDetails?.paymentOrderResponse?.hotel?.phones;
        if (phones && phones.length > 0) {
            return phones.map(phone => phone.phoneNumber).join(', ');
        };
        return "N/A";
    };

    const getHotelEmail = () => {
        return ticketDetails?.paymentOrderResponse?.hotel?.email || "N/A";
    };

    const getCheckInDate = () => {
        if (ticketDetails?.paymentOrderResponse?.hotelOrderDetail?.checkIn) {
            return moment(ticketDetails.paymentOrderResponse.hotelOrderDetail.checkIn).format("DD MMM YYYY");
        };
        return "N/A";
    };

    const getCheckOutDate = () => {
        if (ticketDetails?.paymentOrderResponse?.hotelOrderDetail?.checkOut) {
            return moment(ticketDetails.paymentOrderResponse.hotelOrderDetail.checkOut).format("DD MMM YYYY");
        };
        return "N/A";
    };

    const getNightCount = () => {
        if (ticketDetails?.paymentOrderResponse?.hotelOrderDetail?.checkIn &&
            ticketDetails?.paymentOrderResponse?.hotelOrderDetail?.checkOut) {
            return moment(ticketDetails.paymentOrderResponse.hotelOrderDetail.checkOut)
                .diff(moment(ticketDetails.paymentOrderResponse.hotelOrderDetail.checkIn), 'days');
        };
        return "N/A";
    };


    // API CALLS
    const getUserDetails = async () => {
        if (!ticketDetails?.id) return;

        const response = await getUsersData(`/details/invoice?bookingId=${ticketDetails.id}&category=HOTEL`);

        if (response.isSuccess) {
            setUserDetails(response.data?.data);
        } else {
            console.log("Error while fetching user details: ", response.data);
        };
    };


    return (
        <div className="p-6 max-w-5xl mx-auto bg-white rounded-lg print:shadow-none">
            {/* HEADER */}
            <div className="flex justify-between items-start pb-6 border-b border-gray-300">
                <div className="text-gray-800">
                    <img
                        className="h-20 w-auto mb-6"
                        src={require("../../../../../../../assets/logos/travelyatra.jpeg")}
                        alt="Paul Merchants Logo"
                    />
                    <p className="text-base font-semibold">Paul Merchants Ltd.</p>
                    <div className="text-sm flex items-center gap-1">
                        SCO 829-830, Sector 22A Chandigarh, Chandigarh
                    </div>
                    <div className="text-sm">FE.CG/AD-II/01/2008 NDL-ADII-0001-2023</div>
                    <div className="text-sm mt-2">
                        <span className="font-semibold">GSTIN No: </span>
                        <span>04AAACP5609D1ZW</span>
                        <span className="font-semibold ml-1"> CIN No: </span>
                        <span>L74900DL1984PLC018679</span>
                    </div>
                    <div className="text-sm mt-2 flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                            <span className="font-semibold">Contact: </span>
                            {/* <Phone className="size-3.5" /> */}
                            <span>0172 - 5041735, 01725041735</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="font-semibold">Email: </span>
                            {/* <Mail className="size-3.5" /> */}
                            <span>del.travelaccounts@paulmerchants.net</span>
                        </div>
                    </div>
                </div>
                <div className="text-right flex flex-col justify-end items-end text-gray-700 text-sm">
                    <div className="text-4xl font-bold tracking-tight text-gray-800 mb-2">
                        Payment Advice
                    </div>
                    {ticketDetails?.invoiceNo && (
                        <div>
                            No.: {ticketDetails.invoiceNo}
                        </div>
                    )}
                    <div>
                        Issue on{" "}
                        {ticketDetails?.bookingDate
                            ? moment(ticketDetails.bookingDate).format("DD/MM/YYYY")
                            : "N/A"}
                    </div>
                </div>
            </div>

            {/* Customer Details */}
            <div className="my-12">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-sm text-gray-600 mb-1 font-medium">To,</p>
                        <p className="text-lg font-semibold text-gray-800">
                            {userDetails?.name || "N/A"}
                        </p>
                        {userDetails?.affiliateDetailForInvoice?.companyName && (
                            <p className="font-semibold text-gray-800">
                                {userDetails.affiliateDetailForInvoice.companyName}
                            </p>
                        )}
                        <p className="text-sm text-gray-600 mt-1">
                            {userDetails?.emailId || "N/A"}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                            {userDetails?.phoneNumber || "N/A"}
                        </p>
                        {userDetails?.affiliateDetailForInvoice?.firstAddress && (
                            <>
                                <p className="text-sm text-gray-600 mt-1">
                                    {[
                                        userDetails.affiliateDetailForInvoice.firstAddress,
                                        userDetails.affiliateDetailForInvoice.secondAddress,
                                        userDetails.affiliateDetailForInvoice.city,
                                        userDetails.affiliateDetailForInvoice.state,
                                        userDetails.affiliateDetailForInvoice.pinCode
                                    ].filter(Boolean).join(', ')}
                                </p>
                                <p className="text-sm font-medium text-gray-700">
                                    Place of Supply:{" "}
                                    <span className="text-gray-600 font-normal">
                                        {[
                                            userDetails.affiliateDetailForInvoice.state,
                                            userDetails.affiliateDetailForInvoice.country
                                        ].filter(Boolean).join(', ') || "N/A"}
                                    </span>
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Transaction Details */}
            <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                    <div className="h-5 w-1 bg-blue-500 rounded-full"></div>
                    <p className="text-base font-semibold text-gray-800">
                        Transaction Details: Hotel Accommodation
                    </p>
                </div>

                {/* Table with hotel booking info */}
                <div className="overflow-x-auto rounded-md border border-gray-300">
                    <ETable>
                        <thead>
                            <tr className="bg-gray-100 text-left">
                                <th className="p-2 border-b border-gray-300">
                                    Hotel Name
                                </th>
                                <th className="p-2 border-b border-gray-300">
                                    Booking Reference
                                </th>
                                <th className="p-2 border-b border-gray-300">
                                    Check In - Check Out
                                </th>
                                <th className="p-2 border-b border-gray-300">
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="p-2 text-sm text-gray-9000">
                                    <div className="font-medium">{getHotelName()}</div>
                                    <div className="text-gray-600 mt-1 flex items-center gap-1 text-xs">
                                        {/* <MapPin className="size-3" /> */}
                                        {getHotelAddress()}
                                    </div>
                                    <div className="text-gray-600 text-xs mt-1">
                                        <span className="text-slate-500">Contact No. </span>
                                        {getHotelPhones()}
                                    </div>
                                    <div className="text-gray-600 text-xs lowercase">
                                        <span className="capitalize">Email: </span>
                                        {getHotelEmail()}
                                    </div>
                                </td>
                                <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                    <div className="font-medium">{ticketDetails?.referenceNo || "N/A"}</div>
                                    {/* <div className="text-gray-600 text-xs">Confirmation: {ticketDetails?.confirmationId || "N/A"}</div> */}
                                </td>
                                <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                    <div className="flex flex-col gap-1 font-medium">
                                        <div className="flex items-center gap-1">
                                            {/* <CalendarRange className="size-3.5 text-gray-500" /> */}
                                            {getCheckInDate()}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {/* <CalendarRange className="size-3.5 text-gray-500" /> */}
                                            {getCheckOutDate()}
                                        </div>
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                        {getNightCount()} Night(s)
                                    </div>
                                </td>
                                <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                    <div className="font-medium text-blue-600">
                                        {humanizeText(ticketDetails?.bookingStatus || "")}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </ETable>
                </div>
            </div>

            {/* Room Details */}
            {ticketDetails?.bookingRooms && ticketDetails.bookingRooms.length > 0 && (
                <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3 html2pdf__page-break--avoid">
                        <div className="h-5 w-1 bg-blue-500 rounded-full"></div>
                        <p className="text-base font-semibold text-gray-800">
                            Room Details
                        </p>
                    </div>

                    <div className="rounded-md border border-gray-300 p-3">
                        <HotelRoomDetailsSimple
                            hotel={ticketDetails.bookingRooms}
                            showCarousel={false}
                        />
                    </div>
                </div>
            )}

            {/* Guest Details */}
            {ticketDetails?.bookingRooms && ticketDetails.bookingRooms.some(room => room.guestList && room.guestList.length > 0) && (
                <div className="mb-6  html2pdf__page-break--avoid">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="h-5 w-1 bg-blue-500 rounded-full"></div>
                        <p className="text-base font-semibold text-gray-800">
                            Guest Details
                        </p>
                    </div>

                    <div className="overflow-x-auto rounded-md border border-gray-300">
                        <ETable>
                            <thead>
                                <tr className="bg-gray-100 text-left html2pdf__page-break--avoid">
                                    <th className="p-2 border-b border-gray-300">
                                        No.
                                    </th>
                                    <th className="p-2 border-b border-gray-300">
                                        Name
                                    </th>
                                    <th className="p-2 border-b border-gray-300">
                                        ID Type
                                    </th>
                                    <th className="p-2 border-b border-gray-300">
                                        ID Number
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {ticketDetails.bookingRooms.flatMap(
                                    (room, roomIndex) =>
                                        (room.guestList || []).map(
                                            (guest, guestIndex) => {
                                                // calculate guest number
                                                let guestCount = 0;
                                                for (let i = 0; i < roomIndex; i++) {
                                                    guestCount += (ticketDetails.bookingRooms[i]?.guestList?.length || 0);
                                                };
                                                const guestNumber = guestCount + guestIndex + 1;

                                                return (
                                                    <tr key={`guest-${roomIndex}-${guestIndex}`} className="html2pdf__page-break--avoid">
                                                        <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                            {guestNumber}
                                                        </td>
                                                        <td className="p-2 text-sm text-gray-900 border-b border-gray-300 font-medium">
                                                            <span className="capitalize">{guest?.salutation?.toLowerCase() || ""}</span>{" "}
                                                            {guest?.firstName || ""}{" "}
                                                            {guest?.lastName || ""}
                                                        </td>
                                                        <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                            {guest?.panCardNo ? "PAN" : guest?.passPortNumber ? "Passport" : "N/A"}
                                                        </td>
                                                        <td className="p-2 text-sm text-gray-900 border-b border-gray-300">
                                                            {guest?.panCardNo || guest?.passPortNumber || "N/A"}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                )}
                            </tbody>
                        </ETable>
                    </div>
                </div>
            )}

            {/* Amount Summary with Fare Breakdown - Similar to Flight Invoice */}
            <div className="flex justify-between items-end gap-4 mb-8 html2pdf__page-break--avoid">
                <div className="w-6/12 h-fit">
                    <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-gray-800">
                            Amount in words:
                            <div className="font-normal text-gray-600">
                                {numberToWords(fareDetails.totalFare)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end w-5/12">
                    <div className="w-full border border-gray-300 rounded-md p-3">
                        <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-300">
                            Payment Summary
                        </h3>

                        <div className="mb-4">
                            <h4 className="text-sm font-medium text-gray-800 mb-2">
                                Fare Breakdown:
                            </h4>
                            <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-300 space-y-1.5">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Basic Fare</span>
                                    <span className="text-gray-800">
                                        <NumberFormatter
                                            thousandSeparator
                                            prefix={"₹"}
                                            value={fareDetails.hotelBaseFare.toFixed(2)}
                                        />
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Hotel Tax & Other</span>
                                    <span className="text-gray-800">
                                        <NumberFormatter
                                            thousandSeparator
                                            prefix={"₹"}
                                            value={fareDetails.hotelTax}
                                        />
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm font-medium pt-1.5 mt-1 border-t border-gray-300">
                                    <span>Room Amount</span>
                                    <span>
                                        <NumberFormatter
                                            thousandSeparator
                                            prefix={"₹"}
                                            value={(fareDetails.hotelBaseFare + fareDetails.hotelTax).toFixed(2)}
                                        />
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex justify-between">
                                <p className="text-sm text-gray-600">Service Charges</p>
                                <p className="text-sm font-medium text-gray-800">
                                    <NumberFormatter
                                        thousandSeparator
                                        prefix={"₹"}
                                        value={fareDetails.serviceCharges}
                                    />
                                </p>
                            </div>
                            <div className="flex justify-between">
                                <p className="text-sm text-gray-600">Other Charge</p>
                                <p className="text-sm font-medium text-gray-800">
                                    <NumberFormatter
                                        thousandSeparator
                                        prefix={"₹"}
                                        value={fareDetails.otherCharges}
                                    />
                                </p>
                            </div>
                            <div className="flex justify-between">
                                <p className="text-sm text-gray-600">GST On Charges</p>
                                <p className="text-sm font-medium text-gray-800">
                                    <NumberFormatter
                                        thousandSeparator
                                        prefix={"₹"}
                                        value={fareDetails.totalGst}
                                    />
                                </p>
                            </div>
                            <div className="flex justify-between font-bold text-gray-800 pt-2 mt-2 border-t border-gray-300">
                                <p>Total Amount</p>
                                <NumberFormatter
                                    thousandSeparator
                                    prefix={"₹"}
                                    value={fareDetails.totalFare.toFixed(2)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payment Instructions */}
            <div className="mb-6 text-sm border-t border-gray-300 pt-4 text-gray-800  html2pdf__page-break--avoid">
                <p className="mb-1"><span className="font-medium">Cash:</span> Payment should be made directly to chief cashier in our office and to no one else.</p>
                <p className="mb-1"><span className="font-medium">Receipts:</span> Receipt on our official receipt form duly signed by our cashier only will be considered valid.</p>
                <p className="mb-1"><span className="font-medium">Cheque:</span> All cheques should be drawn in favour of Paul Merchants Ltd and crossed a/c payee.</p>
                <p className="mb-1"><span className="font-medium">Outstandings:</span> We reserve the right to charge interest @ 24% per annum on all outstanding overdue account.</p>
                <p className="mt-2 mb-1"><span className="font-medium">All payments to be transferred directly into below account</span></p>
                <p className="mb-1"><span className="font-medium">HDFC account number 00350330000694 IFSC HDFC0000035</span></p>
                <p className="text-xs">HSN/SAC-996761</p>
            </div>

            {/* FOOTER */}
            <div className="border-t border-gray-300 pt-4 mt-6">
                <div className="flex justify-between items-center">
                    <div className="w-24 h-24">
                        <QRCode
                            size={96}
                            value={`Hotel Invoice: ${ticketDetails?.invoiceNo || "N/A"}`}
                            className="h-full w-full"
                        />
                    </div>
                    <p className="text-center text-gray-800 text-xs font-medium">
                        This is a computer generated Invoice and does not require
                        Signature/Stamp
                    </p>
                    <div className="text-right text-xs text-gray-800">
                        <p>Generated on: {new Date().toLocaleDateString()}</p>
                        <p className="mt-1">Paul Merchants Ltd.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
